import Papa from 'papaparse';

export interface ParsedFile {
  file: File;
  parsedName: ParsedFileName;
  detectedConfig?: {
    tp_usd: number | null;
    sl_usd: number | null;
    quantity: number | null;
    instrument: string;
  };
  csvData?: any[];
  error?: string;
}

export interface ParsedFileName {
  symbol: string;
  risk?: string;
  timeframe?: string;
  tp?: number;
  sl?: number;
  param1?: number;
  param2?: number;
  originalName: string;
}

export interface BotMatchResult {
  botId: string;
  botName: string;
  confidence: number;
  matchType: 'exact' | 'symbol' | 'substring';
  parsedBot: ParsedBotName;
}

export interface ParsedBotName {
  symbol: string;
  risk?: string;
  timeframe?: string;
  originalName: string;
}

export interface FileMapping {
  file: ParsedFile;
  suggestedBot?: BotMatchResult;
  selectedBotId?: string;
  enabled: boolean;
  mappingId?: string;
}

export class BulkHistoricalUploadService {
  // Normalize symbols for matching (e.g., APTUSDT.P -> APTUSDT)
  static normalizeSymbol(symbol: string): string {
    return (symbol || '').toUpperCase().replace(/\s+/g, '').replace(/\.P$/i, '');
  }

  // Parse filename pattern: SYMBOL-RISK-TIMEFRAME.xlsx_TP_SL_PARAM1_PARAM2
  static parseFileName(filename: string): ParsedFileName {
    const result: ParsedFileName = {
      symbol: '',
      originalName: filename
    };

    try {
      // Remove file extensions - handle .xlsx.csv or embedded .xlsx/.csv patterns
      let nameWithoutExt = filename.replace(/\.(xlsx?|csv)$/i, '');
      nameWithoutExt = nameWithoutExt.replace(/\.xlsx?/gi, ''); // Remove any embedded .xlsx/.xls
      nameWithoutExt = nameWithoutExt.replace(/\.csv/gi, ''); // Remove any embedded .csv
      
      // Split by underscore to separate main name from TP/SL parameters
      const parts = nameWithoutExt.split('_');
      const mainPart = parts[0];
      
      // Parse main part: SYMBOL-RISK-TIMEFRAME
      const mainMatch = mainPart.match(/^([A-Z]{3,}\.?P?)-([0-9.]+)-([0-9]+[MS]?)$/i);
      if (mainMatch) {
        result.symbol = mainMatch[1].toUpperCase();
        result.risk = mainMatch[2];
        result.timeframe = mainMatch[3].toUpperCase();
      } else {
        // Fallback: extract symbol from beginning
        const symbolMatch = mainPart.match(/^([A-Z]{3,}\.?P?)/i);
        result.symbol = symbolMatch ? symbolMatch[1].toUpperCase() : '';
      }

      // Parse TP/SL parameters if available
      if (parts.length >= 3) {
        const tp = parseFloat(parts[1]);
        const sl = parseFloat(parts[2]);
        if (!isNaN(tp)) result.tp = tp;
        if (!isNaN(sl)) result.sl = sl;
      }

      // Parse additional parameters
      if (parts.length >= 5) {
        const param1 = parseFloat(parts[3]);
        const param2 = parseFloat(parts[4]);
        if (!isNaN(param1)) result.param1 = param1;
        if (!isNaN(param2)) result.param2 = param2;
      }
    } catch (error) {
      console.warn('Error parsing filename:', filename, error);
    }

    return result;
  }

  // Parse bot name pattern: SYMBOL-RISK-TIMEFRAME or SYMBOL.P-RISK-TIMEFRAME
  static parseBotName(botName: string): ParsedBotName {
    const result: ParsedBotName = {
      symbol: '',
      originalName: botName
    };

    try {
      const match = botName.match(/^([A-Z]{3,}\.?P?)-([0-9.]+)-([0-9]+[MS]?)$/i);
      if (match) {
        result.symbol = match[1].toUpperCase();
        result.risk = match[2];
        result.timeframe = match[3].toUpperCase();
      } else {
        // Fallback: extract symbol from beginning
        const symbolMatch = botName.match(/^([A-Z]{3,}\.?P?)/i);
        result.symbol = symbolMatch ? symbolMatch[1].toUpperCase() : '';
      }
    } catch (error) {
      console.warn('Error parsing bot name:', botName, error);
    }

    return result;
  }

  // Calculate match confidence between file and bot
  static calculateMatchConfidence(parsedFile: ParsedFileName, parsedBot: ParsedBotName): number {
    let confidence = 0;

    const fileSym = this.normalizeSymbol(parsedFile.symbol);
    const botSym = this.normalizeSymbol(parsedBot.symbol);

    // Symbol match (most important - but not everything)
    if (fileSym && botSym) {
      if (fileSym === botSym) {
        confidence += 50; // Reduced from 80 to leave room for timeframe/risk
      } else if (fileSym.includes(botSym) || botSym.includes(fileSym)) {
        confidence += 30; // Reduced from 50
      }
    }

    // Timeframe match (critical - if different, heavily penalize)
    if (parsedFile.timeframe && parsedBot.timeframe) {
      if (parsedFile.timeframe.toUpperCase() === parsedBot.timeframe.toUpperCase()) {
        confidence += 35; // Increased importance
      } else {
        // Major penalty for timeframe mismatch - cap total at 30
        confidence = Math.min(confidence, 30);
        return confidence;
      }
    } else if (!parsedFile.timeframe || !parsedBot.timeframe) {
      // Missing timeframe data - slight penalty
      confidence -= 5;
    }

    // Risk match (important for exact matching)
    if (parsedFile.risk && parsedBot.risk) {
      const rf = parseFloat(parsedFile.risk);
      const rb = parseFloat(parsedBot.risk);
      if (!isNaN(rf) && !isNaN(rb)) {
        if (Math.abs(rf - rb) < 0.001) confidence += 15; // exact match - increased
        else if (Math.abs(rf - rb) <= 0.1) confidence += 8; // close match
        else if (Math.abs(rf - rb) <= 0.5) confidence += 3; // somewhat close
        else confidence -= 10; // major mismatch penalty
      } else if (parsedFile.risk === parsedBot.risk) {
        confidence += 15;
      }
    } else if (!parsedFile.risk || !parsedBot.risk) {
      // Missing risk data - slight penalty
      confidence -= 3;
    }

    return Math.min(Math.max(confidence, 0), 100);
  }

  // Find best bot match for a parsed file
  static findBestBotMatch(
    parsedFile: ParsedFileName, 
    availableBots: Array<{ id: string; name: string }>
  ): BotMatchResult | null {
    let bestMatch: BotMatchResult | null = null;
    let maxConfidence = 0;

    for (const bot of availableBots) {
      const parsedBot = this.parseBotName(bot.name);
      const confidence = this.calculateMatchConfidence(parsedFile, parsedBot);

      if (confidence > maxConfidence) {
        maxConfidence = confidence;
        
        let matchType: 'exact' | 'symbol' | 'substring' = 'substring';
        if (confidence >= 85) matchType = 'exact';
        else if (confidence >= 60) matchType = 'symbol';

        bestMatch = {
          botId: bot.id,
          botName: bot.name,
          confidence,
          matchType,
          parsedBot
        };
      }
    }

    // Only return matches with reasonable confidence
    return bestMatch && bestMatch.confidence >= 40 ? bestMatch : null;
  }

  // Parse CSV file and detect configuration
  static async parseCSVFile(file: File): Promise<{
    csvData: any[];
    detectedConfig?: {
      tp_usd: number | null;
      sl_usd: number | null;
      quantity: number | null;
      instrument: string;
    };
    error?: string;
  }> {
    try {
      const text = await file.text();
      
      const parsed = Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
      });

      if (parsed.errors.length > 0) {
        console.warn('CSV parsing errors:', parsed.errors);
      }

      let detectedConfig;
      
      // Check for CSV Analysis metadata in first row
      const headers = Object.keys(parsed.data[0] || {});
      const hasConfigFlag = headers.includes('__CSV_ANALYSIS_CONFIG__');
      
      if (hasConfigFlag && parsed.data.length > 0) {
        const firstRow = parsed.data[0] as any;
        
        const safeParseFloat = (value: any) => {
          if (value === null || value === undefined || value === '') return null;
          const parsed = parseFloat(value);
          return isNaN(parsed) ? null : parsed;
        };

        detectedConfig = {
          tp_usd: safeParseFloat(firstRow['__TP_USD__']),
          sl_usd: safeParseFloat(firstRow['__SL_USD__']),
          quantity: safeParseFloat(firstRow['__QUANTITY_BASE__']),
          instrument: firstRow['__INSTRUMENT__'] || ''
        };
      }

      // Filter out metadata columns
      const filteredData = parsed.data.map((row: any) => {
        const filteredRow = { ...row };
        delete filteredRow['__TP_USD__'];
        delete filteredRow['__SL_USD__'];
        delete filteredRow['__QUANTITY_BASE__'];
        delete filteredRow['__INSTRUMENT__'];
        delete filteredRow['__CSV_ANALYSIS_CONFIG__'];
        return filteredRow;
      });

      // Normalize data format
      const normalizedData = filteredData.map((row: any) => {
        const resolveNumber = (keys: string[]): number => {
          for (const key of keys) {
            const value = row[key];
            if (value !== undefined && value !== null && value !== '') {
              const parsed = parseFloat(value.toString().replace(/[^-0-9.]/g, ''));
              if (!isNaN(parsed)) return parsed;
            }
          }
          return 0;
        };

        const signal = row['Signal'] || row['signal'] || '';
        const pnlKeys = ['Net P&L USD', 'Net P&L USDT', 'P&L USD', 'P&L USDT', 'Net P&L', 'P&L', 'pnl'];
        const pnl = resolveNumber(pnlKeys);
        const dateStr = row['Date/Time'] || row['datetime'] || row['timestamp'] || '';
        const priceKeys = ['Price USD', 'Price USDT', 'Price', 'price'];
        const price = resolveNumber(priceKeys);
        const positionKeys = ['Position size (qty)', 'Position size qty', 'Position size', 'Qty', 'position_size'];
        const positionSize = resolveNumber(positionKeys);
        const cumulativeKeys = ['Cumulative P&L USD', 'Cumulative P&L USDT', 'Cumulative P&L', 'cumulative_pnl'];
        const cumulativePnl = resolveNumber(cumulativeKeys);

        return {
          timestamp: dateStr,
          win: pnl > 0 ? 1 : 0,
          pnl,
          signal,
          price,
          position_size: positionSize,
          cumulative_pnl: cumulativePnl
        };
      });

      return {
        csvData: normalizedData,
        detectedConfig
      };
    } catch (error) {
      return {
        csvData: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Process multiple files for bulk upload
  static async processFiles(
    files: File[], 
    availableBots: Array<{ id: string; name: string }>
  ): Promise<FileMapping[]> {
    const results: FileMapping[] = [];

    for (const file of files) {
      const parsedName = this.parseFileName(file.name);
      
      // Parse CSV file
      const { csvData, detectedConfig, error } = await this.parseCSVFile(file);
      
      const parsedFile: ParsedFile = {
        file,
        parsedName,
        detectedConfig,
        csvData,
        error
      };

      // Find best bot match
      const suggestedBot = this.findBestBotMatch(parsedName, availableBots);

      results.push({
        file: parsedFile,
        suggestedBot: suggestedBot || undefined,
        selectedBotId: suggestedBot?.botId,
        enabled: !!suggestedBot && suggestedBot.confidence >= 70 // Auto-enable only high confidence matches
      });
    }

    return results;
  }

  // Get confidence level display
  static getConfidenceDisplay(confidence: number): { label: string; color: string } {
    if (confidence >= 85) return { label: 'Excellent', color: 'text-green-600' };
    if (confidence >= 70) return { label: 'Good', color: 'text-blue-600' };
    if (confidence >= 50) return { label: 'Fair', color: 'text-yellow-600' };
    return { label: 'Poor', color: 'text-red-600' };
  }
}